import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, ShoppingBag, TrendingUp, PlayCircle, StopCircle, X, CreditCard } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { shiftApi } from '../api';
import ShiftStartDialog from '../components/shift/ShiftStartDialog';
import ShiftEndDialog from '../components/shift/ShiftEndDialog';
import ShiftHistoryTable from '../components/shift/ShiftHistoryTable';

const ShiftPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  const [currentShift, setCurrentShift] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [detailShift, setDetailShift] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadCurrentShift();
    loadHistory();
  }, []);

  const loadCurrentShift = async () => {
    try {
      const res = await shiftApi.getCurrent();
      if (res.success && res.data) setCurrentShift(res.data);
    } catch (err) { console.error('获取当前班次失败:', err); }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await shiftApi.getHistory({ limit: 20 });
      if (res.success) setHistory(res.data || []);
    } catch (err) { console.error('获取班次历史失败:', err); }
    finally { setLoading(false); }
  };

  const handleStartShift = async (data) => {
    try {
      const res = await shiftApi.start(data);
      if (res.success) {
        alert(language === 'en' ? 'Shift started successfully' : '班次开始成功');
        setStartDialogOpen(false);
        loadCurrentShift();
      }
    } catch (err) {
      alert(language === 'en' ? 'Failed to start shift' : '开始班次失败');
    }
  };

  const handleEndShift = async (data) => {
    try {
      const res = await shiftApi.end(data);
      if (res.success) {
        alert(language === 'en' ? 'Shift ended successfully' : '班次结束成功');
        setEndDialogOpen(false);
        setCurrentShift(null);
        loadHistory();
      }
    } catch (err) {
      alert(language === 'en' ? 'Failed to end shift' : '结束班次失败');
    }
  };

  const handleViewDetail = async (shiftId) => {
    setDetailLoading(true);
    setDetailShift({ loading: true });
    try {
      const res = await shiftApi.getDetail(shiftId);
      if (res.success) setDetailShift(res.data);
    } catch (err) {
      console.error('获取班次详情失败:', err);
      setDetailShift(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const getDuration = (startTs, endTs) => {
    if (!startTs) return '-';
    const end = endTs || Math.floor(Date.now() / 1000);
    const diff = end - startTs;
    if (diff < 0) return '-';
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${hours}${language === 'en' ? 'h' : '小时'} ${minutes}${language === 'en' ? 'm' : '分钟'}`;
  };

  const PAYMENT_LABELS = {
    cash: '现金',
    card: '刷卡',
    khqr: 'KHQR',
    alipay: '支付宝',
    hold: '挂单',
  };

  return (
    <div className="h-full flex flex-col gap-4">

      {/* 头部 */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">
            {language === 'en' ? 'Shift Management' : '班次管理'}
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            {language === 'en' ? 'Manage your work shifts and cash register' : '管理您的工作班次和收银台'}
          </p>
        </div>
        {currentShift ? (
          <button onClick={() => setEndDialogOpen(true)}
            className="inline-flex items-center space-x-1 bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium">
            <StopCircle className="w-4 h-4" />
            <span>{language === 'en' ? 'End Shift' : '结束班次'}</span>
          </button>
        ) : (
          <button onClick={() => setStartDialogOpen(true)}
            className="inline-flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <PlayCircle className="w-4 h-4" />
            <span>{language === 'en' ? 'Start Shift' : '开始班次'}</span>
          </button>
        )}
      </div>

      {/* 当前班次 */}
      <div className="flex-shrink-0">
        {currentShift ? (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-48">
                <div className="flex items-center space-x-2 mb-1">
                  <Clock className="w-4 h-4" />
                  <h3 className="text-sm font-bold">{language === 'en' ? 'Current Shift' : '当前班次'}</h3>
                </div>
                <p className="text-blue-100 text-xs">{language === 'en' ? 'Cashier' : '收银员'}: {currentShift.cashier_name}</p>
                <p className="text-blue-100 text-xs">
                  {language === 'en' ? 'Start' : '开始'}: {currentShift.start_ts ? new Date(currentShift.start_ts * 1000).toLocaleString() : '-'}
                </p>
                <p className="text-blue-100 text-xs">
                  {language === 'en' ? 'Duration' : '时长'}: {getDuration(currentShift.start_ts, null)}
                </p>
              </div>

              <div className="flex-1 grid grid-cols-3 gap-5">
                {[
                  { label: language === 'en' ? 'Opening Cash' : '期初金额', value: `$${parseFloat(currentShift.starting_cash || 0).toFixed(2)}`, icon: DollarSign },
                  { label: language === 'en' ? 'Total Sales' : '销售总额', value: `$${parseFloat(currentShift.total_sales || 0).toFixed(2)}`, icon: TrendingUp },
                  { label: language === 'en' ? 'Transactions' : '交易数量', value: currentShift.order_count || 0, icon: ShoppingBag },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="bg-white/10 rounded-lg p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-xs">{label}</p>
                        <p className="text-lg font-bold mt-0.5">{value}</p>
                      </div>
                      <Icon className="w-5 h-5 text-blue-200" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
            <Clock className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              {language === 'en' ? 'No Active Shift' : '没有活动班次'}
            </h3>
            <p className="text-xs text-gray-500">
              {language === 'en' ? 'Click top right to start a shift' : '请点击右上角开始班次'}
            </p>
          </div>
        )}
      </div>

      {/* 班次历史 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 border-b">
          <h3 className="text-base font-bold">{language === 'en' ? 'Shift History' : '班次历史'}</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ShiftHistoryTable shifts={history} onViewDetail={handleViewDetail} />
          )}
        </div>
      </div>

      {/* 班次详情弹窗 */}
      {detailShift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">
                {language === 'en' ? 'Shift Detail' : '班次明细'}
                {detailShift.start_ts && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    {new Date(detailShift.start_ts * 1000).toLocaleDateString()}
                  </span>
                )}
              </h3>
              <button onClick={() => setDetailShift(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {detailShift.loading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5 space-y-4">

                {/* 班次汇总 */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500">期初金额</p>
                    <p className="text-lg font-bold text-blue-600">${parseFloat(detailShift.starting_cash || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500">销售总额</p>
                    <p className="text-lg font-bold text-green-600">${parseFloat(detailShift.total_sales || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500">期末金额</p>
                    <p className="text-lg font-bold text-purple-600">${parseFloat(detailShift.ending_cash || 0).toFixed(2)}</p>
                  </div>
                </div>

                {/* 支付方式汇总 */}
                {detailShift.payment_summary?.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />支付方式汇总
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {detailShift.payment_summary.map(p => (
                        <div key={p.payment_method} className="flex justify-between items-center bg-white rounded-lg px-3 py-2">
                          <span className="text-sm text-gray-600">{PAYMENT_LABELS[p.payment_method] || p.payment_method}</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-green-600">${parseFloat(p.total).toFixed(2)}</span>
                            <span className="text-xs text-gray-400 ml-1">({p.count}笔)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 销售明细 */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    销售明细（{detailShift.sales?.length || 0}笔）
                  </p>
                  {detailShift.sales?.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">暂无销售记录</p>
                  ) : (
                    <div className="space-y-2">
                      {detailShift.sales?.map(sale => (
                        <div key={sale.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{sale.order_no}</p>
                            <p className="text-xs text-gray-400">{sale.items_summary}</p>
                            <p className="text-xs text-gray-400">
                              {sale.created_at ? new Date(sale.created_at).toLocaleString([], {
                                month: '2-digit', day: '2-digit',
                                hour: '2-digit', minute: '2-digit'
                              }) : '-'}
                              {' · '}{PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                            </p>
                          </div>
                          <p className="text-sm font-bold text-green-600">
                            ${parseFloat(sale.total_amount).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {detailShift.notes && (
                  <div className="bg-yellow-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">备注</p>
                    <p className="text-sm text-gray-700 mt-1">{detailShift.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {startDialogOpen && (
        <ShiftStartDialog onClose={() => setStartDialogOpen(false)} onSubmit={handleStartShift} />
      )}
      {endDialogOpen && currentShift && (
        <ShiftEndDialog shift={currentShift} onClose={() => setEndDialogOpen(false)} onSubmit={handleEndShift} />
      )}
    </div>
  );
};

export default ShiftPage;